import { getMissingStockAllowanceError } from '@features/stock/entity';
import { VisitForm, VisitDetail, getVisitFinishErrors } from '@features/visits/entity';
import { useVisitStatusMutation } from '@features/visits/queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useState, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

export const useVisitDetailStatusSwitch = ({
  methods,
  visitDetail,
}: {
  methods: UseFormReturn<VisitForm>;
  visitDetail: Pick<VisitDetail, 'visit' | 'procedures' | 'client'>;
}) => {
  const { control, setValue } = methods;
  const { visit, procedures, client } = visitDetail;

  const { t } = useIntlTranslations();
  const [isSwitchOn, setIsSwitchOn] = useState(visitDetail.visit?.visitStatus);
  const visitCloseDialogRef = useRef<ImperativeFormDialogRefType | null>(null);

  const { mutate: changeVisitStatus } = useVisitStatusMutation();

  const paidPrice = useWatch({ control, name: 'paidPrice' });
  const deposit = useWatch({ control, name: 'deposit' });
  const depositStatus = useWatch({ control, name: 'depositStatus' });

  const errors = getVisitFinishErrors(client.deposit, { paidPrice, deposit, depositStatus }, t);
  const missingStockAllowanceError = procedures && getMissingStockAllowanceError(t, procedures);

  const onConfirm = () => {
    setIsSwitchOn(true);
    visitCloseDialogRef.current?.close();
    setValue('visitClosed', true);
    changeVisitStatus({ visitId: visit.id, status: true });
  };

  const handleChange = (_: unknown, state: boolean) => {
    if (state) {
      const hasErrors = errors.length > 0 || !!missingStockAllowanceError;
      if (hasErrors) {
        visitCloseDialogRef.current?.open();
      } else {
        setIsSwitchOn(true);
        setValue('visitClosed', true);
        changeVisitStatus({ visitId: visit.id, status: true });
      }
    } else {
      setIsSwitchOn(false);
      setValue('visitClosed', false);
      changeVisitStatus({ visitId: visit.id, status: false });
    }
  };

  return {
    isSwitchOn,
    errors,
    missingStockAllowanceError,
    setIsSwitchOn,
    visitCloseDialogRef,
    onConfirm,
    handleChange,
    t,
  };
};
